import React, {useEffect, useState} from "react";
import {Stack, Box, Paper, CircularProgress} from "@mui/material";
import CustomButton from "../../../core/components/ui/CustomButton";
import CustomTypography from "../../../core/components/ui/CustomTypography";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {useNavigate, useLocation} from "react-router-dom";
import useVehicle from "../hooks/useVehicle";
import CustomTextField from "../../../core/components/ui/CustomTextfield";
import VehicleTypesDropdownComponent from "../common/vehicle-type-dropdown.component";


const EditVehicleComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const {vehicleDetails, getViewVehicle, isUpdatingVehicle, updateVehicle} = useVehicle();

    const vehicleId = new URLSearchParams(location.search).get("id");

    const [plateNumberInput, setPlateNumberInput] = useState("");
    const [selectedVehicleType, setSelectedVehicleType] = useState(null);
    const [modelInput, setModelInput] = useState(null);
    const [orcrInput, setOrcrInput] = useState(null);

    const rbacStorage = JSON.parse(localStorage.getItem("rbac-storage"));
    const storedOrgIdFromRBACForBusiness = rbacStorage?.state?.currentProfile?.orgId || null;
    const orgIdFromUrlForPortEmployee = new URLSearchParams(location.search).get("org_uid");


    useEffect(() => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {
                vehicleUID: vehicleId,
                orgUID: organizationId
            };

            getViewVehicle(parameters);

        } else {
            const parameters = {vehicleUID: vehicleId};    
            getViewVehicle(parameters);
        }
    }, [vehicleId, storedOrgIdFromRBACForBusiness, orgIdFromUrlForPortEmployee]);

    useEffect(() => {
        if (vehicleDetails) {
            setPlateNumberInput(vehicleDetails?.plate_number);
            setSelectedVehicleType(vehicleDetails?.type);
            setModelInput(vehicleDetails?.model_manufacturer);
            setOrcrInput(vehicleDetails?.or_cr);
        }
    }, [vehicleDetails])


    const handleVehicleTypeChange = (value) => {
        setSelectedVehicleType(value);
    }

    const handleTextfieldChange = (e, type) => {
        const {value} = e.target;

        switch (type) {
            case "plateNumber":
                setPlateNumberInput(value);
                break;

            case "model":
                setModelInput(value);
                break;

            case "orcr":
                setOrcrInput(value);
                break;

            default:
                break;
        }
    }

    const handleBack = () => {
        if (orgIdFromUrlForPortEmployee) {
            navigate(`/organization/view?org_uid=${orgIdFromUrlForPortEmployee}`);

        } else {
            navigate("/vehicles");
        }
    }

    const handleEditVehicle = () => {
        const organizationId = storedOrgIdFromRBACForBusiness || orgIdFromUrlForPortEmployee;

        if (organizationId) {
            const parameters = {
                orgUID: organizationId
            };

            const payload = {
                vehicle_uid: vehicleDetails?.vehicle_uid,
                plate_number: plateNumberInput,
                type: selectedVehicleType,
                model_manufacturer: modelInput,
                or_cr: orcrInput,
                status: vehicleDetails?.status,
                driver_uid: "",
                container_uid: "",
            };

            updateVehicle(parameters, payload, orgIdFromUrlForPortEmployee);

        } else if (!organizationId) {
            const parameters = null;

            const payload = {
                vehicle_uid: vehicleDetails.vehicle_uid,
                plate_number: plateNumberInput,
                type: selectedVehicleType,
                model_manufacturer: modelInput,
                or_cr: orcrInput,
                status: vehicleDetails.status,
                driver_uid: "",
                container_uid: "",
                org_uid: "",
            };

            updateVehicle(parameters, payload);
        }
    }


    return (
        <>  
            <CustomButton 
                variant="text" 
                startIcon={<ArrowBackIcon fontSize="small" />}
                label="Back to Vehicle List"
                customStyles={{fontSize: 14, ml: -2}}
                onClick={handleBack}
            />

            <Box sx={{m: "auto", maxWidth: "1000px"}}>  
                <Box sx={{textAlign: "start", mt: 2}}>
                    <CustomTypography 
                        variant="h3"
                        text={"Edit Vehicle"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />

                    <CustomTypography 
                        variant="subtitle1"
                        text={"Update vehicle information for entry"}
                        customStyles={{
                            marginBottom: 0, 
                        }}
                    />
                </Box>

                <Paper elevation={0} sx={{border: `1px solid #e5edf6`, borderRadius: 2, padding: "24px 32px 40px", mt: 3}}>
                    <Box sx={{textAlign: "start", mb: 5}}>
                        <CustomTypography 
                            variant="h3"
                            text={"Vehicle Details"}
                            customStyles={{
                                marginBottom: 0, 
                                fontSize: 22,
                                fontWeight: 600
                            }}
                        />

                        <CustomTypography 
                            variant="caption"
                            text={"Fill in the details to update vehicle information"}
                            customStyles={{
                                marginBottom: 0,
                                fontSize: 14 
                            }}
                        />
                    </Box>

                    <Stack direction={{xs: "column", md: "row"}} spacing={4}>
                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Vehicle Type"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                />

                                <VehicleTypesDropdownComponent 
                                    value={selectedVehicleType}
                                    onChange={handleVehicleTypeChange}
                                    fullWidth={true}
                                    size={"medium"}
                                    disabled={isUpdatingVehicle}
                                />
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Model & Manufacturer"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter model & manufacturer"}
                                    variant={"outlined"}
                                    value={modelInput}
                                    onChange={(e) => handleTextfieldChange(e, "model")}
                                    fullWidth={true}
                                    allowedPattern={/[^a-zA-Z0-9\s]/g}
                                    multiline={false}
                                    hiddenLabel
                                    readOnly={isUpdatingVehicle}
                                />  
                            </Box>
                        </Stack>

                        <Stack spacing={2} flex={1}>
                            <Box>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"Plate Number"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter plate number"}
                                    variant={"outlined"}
                                    value={plateNumberInput}
                                    onChange={(e) => handleTextfieldChange(e, "plateNumber")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9-]/g}
                                    hiddenLabel
                                    readOnly={isUpdatingVehicle}
                                />           
                            </Box>

                            <Box sx={{mt: "32px !important"}}>
                                <CustomTypography 
                                    variant="subtitle2"
                                    text={"OR/CR"}
                                    customStyles={{
                                        marginBottom: 1, 
                                    }}
                                /> 
                        
                                <CustomTextField 
                                    placeholder={"Enter OR/CR"}
                                    variant={"outlined"}
                                    value={orcrInput}
                                    onChange={(e) => handleTextfieldChange(e, "orcr")}
                                    fullWidth={true}
                                    multiline={false}
                                    allowedPattern={/[^a-zA-Z0-9-]/g}
                                    hiddenLabel
                                    readOnly={isUpdatingVehicle}
                                />  
                            </Box>
                        </Stack>
                    </Stack>   

                    <Stack direction={{xs: "column", md: "row"}} spacing={2} sx={{mt: 7, justifyContent: "end"}}>
                        <CustomButton 
                            variant={"outlined"}
                            label={"Cancel"}
                            customStyles={{}}
                            onClick={(isUpdatingVehicle) ? null : handleBack}
                        />     

                        <CustomButton 
                            variant={"contained"}
                            label={"Update Vehicle"}
                            startIcon={isUpdatingVehicle && <CircularProgress size="15px" color="#fff" sx={{mr: "2px"}} />}
                            customStyles={{
                                opacity: (plateNumberInput && selectedVehicleType && modelInput && orcrInput && 
                                    (
                                        plateNumberInput !== vehicleDetails?.plate_number ||
                                        selectedVehicleType !== vehicleDetails?.type ||
                                        modelInput !== vehicleDetails?.model_manufacturer ||
                                        orcrInput !== vehicleDetails?.or_cr
                                    ) && !isUpdatingVehicle) ? "100%" : "55%",
                                cursor: (plateNumberInput && selectedVehicleType && modelInput && orcrInput &&  
                                    (
                                        plateNumberInput !== vehicleDetails?.plate_number ||
                                        selectedVehicleType !== vehicleDetails?.type ||
                                        modelInput !== vehicleDetails?.model_manufacturer ||
                                        orcrInput !== vehicleDetails?.or_cr
                                    ) && !isUpdatingVehicle) ? "pointer" : "not-allowed !important",
                            }}
                            onClick={(plateNumberInput && selectedVehicleType && modelInput && orcrInput && 
                                (
                                    plateNumberInput !== vehicleDetails?.plate_number ||
                                    selectedVehicleType !== vehicleDetails?.type ||
                                    modelInput !== vehicleDetails?.model_manufacturer ||
                                    orcrInput !== vehicleDetails?.or_cr
                                ) && !isUpdatingVehicle) ? handleEditVehicle : null}
                        />
                    </Stack>  
                </Paper>
            </Box>
       </>
    )
}

export default EditVehicleComponent